import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const VEHICLE_SEARCH_RESOURCE = "vehicle" as const;
export const VEHICLE_SEARCH_OPERATION = "search" as const;
export const VEHICLE_SEARCH_API_PATH = `/v1/${VEHICLE_SEARCH_RESOURCE}/${VEHICLE_SEARCH_OPERATION}` as const;

export type VehicleSearchParameters = paths[typeof VEHICLE_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type VehicleSearchBody =
  paths[typeof VEHICLE_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type VehicleSearchResponse =
  paths[typeof VEHICLE_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

function sanitizeVehicleSearchBody(body: VehicleSearchBody): VehicleSearchBody {
  const result = { ...body } as Record<string, unknown>;

  delete result.registrationDate;
  delete result.registrationDateRange;
  delete result.mileage;
  delete result.mileageRange;
  delete result.powerKw;
  delete result.powerKwRange;
  delete result.powerPs;
  delete result.powerPsRange;

  if (!result.registrationDateFrom) delete result.registrationDateFrom;
  if (!result.registrationDateTo) delete result.registrationDateTo;

  return result as VehicleSearchBody;
}

export function useVehicleSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<VehicleSearchParameters, VehicleSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<VehicleSearchResponse, Error, VehicleSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<VehicleSearchParameters, VehicleSearchBody>>(options);
  const params = handlePaginatedParameters(optionsWithDefaults.parameters);
  const body = optionsWithDefaults.body;
  const sanitizedBody = body ? sanitizeVehicleSearchBody(body) : body;

  return useQuery({
    queryKey: [VEHICLE_SEARCH_RESOURCE, VEHICLE_SEARCH_OPERATION, { params, body: sanitizedBody }],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<VehicleSearchResponse>(VEHICLE_SEARCH_API_PATH, sanitizedBody, {
        params,
      });

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
